import { useUserSession } from '@solinkcorp/authentication';
import React from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { SDM_SWR_REFRESH_INTERVAL_MS } from '../constants';
import { useSDMClient } from '../contexts/CallhomeClientContext';
import { type ServiceDisplayDayPart } from '../day-part/utils';

function getServiceDisplayDayPartsKey(customerId: string | undefined): string | null {
  return customerId ? `/api/serviceDisplayDayParts?customerId=${customerId}` : null;
}

export function useInvalidateServiceDisplayDayPartsCache() {
  const { mutate } = useSWRConfig();

  return React.useCallback(
    async (customerId: string | undefined) => {
      const key = getServiceDisplayDayPartsKey(customerId);
      if (key) await mutate(key);
    },
    [mutate],
  );
}

export function useServiceDisplayDayParts() {
  const callHomeClient = useSDMClient();
  const { customerId } = useUserSession();
  const key = getServiceDisplayDayPartsKey(customerId);

  return useSWR(
    key,
    () =>
      customerId
        ? callHomeClient.getServiceDisplayDayParts<ServiceDisplayDayPart>({ customerId })
        : Promise.resolve([]),
    {
      suspense: true,
      refreshInterval: SDM_SWR_REFRESH_INTERVAL_MS,
      refreshWhenHidden: true,
    },
  );
}

export function useCreateServiceDisplayDayPart() {
  const callHomeClient = useSDMClient();
  const { customerId } = useUserSession();
  const invalidate = useInvalidateServiceDisplayDayPartsCache();

  return React.useCallback(
    async (data: { label: string; dayParts?: ServiceDisplayDayPart['dayParts'] }) => {
      const created = await callHomeClient.createServiceDisplayDayPart({
        customerId,
        label: data.label,
        dayParts: data.dayParts ?? {},
      });
      await invalidate(customerId ?? undefined);
      return created;
    },
    [callHomeClient, customerId, invalidate],
  );
}

export function useUpdateServiceDisplayDayPart() {
  const callHomeClient = useSDMClient();
  const { customerId } = useUserSession();
  const invalidate = useInvalidateServiceDisplayDayPartsCache();

  return React.useCallback(
    async (id: string, data: Partial<Pick<ServiceDisplayDayPart, 'label' | 'dayParts'>>) => {
      try {
        const updated = await callHomeClient.updateServiceDisplayDayPart(id, data);
        await invalidate(customerId);
        return updated;
      } catch (err) {
        console.error('Failed to update day part schedule:', err);
        return err as Error;
      }
    },
    [callHomeClient, customerId, invalidate],
  );
}

export function useDeleteServiceDisplayDayPart() {
  const callHomeClient = useSDMClient();
  const { customerId } = useUserSession();
  const invalidate = useInvalidateServiceDisplayDayPartsCache();

  return React.useCallback(
    async (id: string) => {
      try {
        await callHomeClient.deleteServiceDisplayDayPart(id);
        await invalidate(customerId ?? undefined);
      } catch (err) {
        console.error('Failed to delete day part:', err);
        return err as Error;
      }
    },
    [callHomeClient, customerId, invalidate],
  );
}
